import { useEffect } from "react";
import {
    useInstallState,
    InstallEyebrow,
    InstallTitle,
    InstallLead,
    InstallActions,
    isPopupShown,
    isStandalone,
    isCompleted,
    markPopupShown,
    persistDismissal,
    keyForMode,
    X,
} from "./installShared";

// One-shot bottom-right popup shown ONLY on the very first visit (per-device).
// After being rendered once — regardless of dismiss/install outcome — the
// `cn_install_popup_shown` flag is set and this component never renders again.
export default function InstallBanner() {
    const {
        mode,
        setMode,
        deferredPrompt,
        installed,
        installing,
        showSamsungFallback,
        setShowSamsungFallback,
        copied,
        triggerNativeInstall,
        openInChrome,
        copyLink,
    } = useInstallState({ respectDismissTTL: true });

    // Hard guards: never render for installed / already-shown / standalone.
    const shouldSkip = installed || isStandalone() || isCompleted() || isPopupShown();

    // Mark the popup as "shown" the FIRST time we actually display a mode.
    useEffect(() => {
        if (shouldSkip) return;
        if (mode) markPopupShown();
    }, [mode, shouldSkip]);

    if (shouldSkip || !mode) return null;

    const dismiss = () => {
        persistDismissal(keyForMode(mode));
        markPopupShown();
        // Hide the popup immediately (Bug B fix): without this the popup stayed
        // on screen until the next reload because no local state changed.
        setMode(null);
        // Notify the home banner so it can appear in the same session.
        try { window.dispatchEvent(new CustomEvent("cn:install-popup-closed")); } catch { /* noop */ }
    };

    const handleNative = async () => {
        const choice = await triggerNativeInstall();
        if (choice && choice.outcome === "accepted") {
            // appinstalled listener will hide us and mark completion.
            markPopupShown();
        } else {
            dismiss();
        }
    };

    const testId =
        mode === "chrome-native" ? "install-banner-native" :
        mode === "chrome-manual" ? "install-banner-chrome-manual" :
        mode === "samsung" ? "install-banner-samsung" :
        "install-banner-ios";

    return (
        <div
            role="dialog"
            aria-label="Installa CondominioNow sulla schermata Home"
            data-testid={testId}
            className="fixed inset-x-3 bottom-3 z-50 rounded-2xl border border-slate-200 bg-white shadow-[0_20px_50px_-20px_rgba(15,76,129,0.35)] p-4 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-500"
            style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
        >
            <button
                type="button"
                onClick={dismiss}
                aria-label="Chiudi"
                data-testid="install-banner-dismiss"
                className="absolute top-2 right-2 size-8 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition flex items-center justify-center"
            >
                <X className="size-4" />
            </button>

            <div className="flex items-start gap-3">
                <img src="/apple-touch-icon.png" alt="CondominioNow" className="size-12 rounded-xl shrink-0 border border-slate-200" width={48} height={48} />
                <div className="min-w-0 pr-6">
                    <InstallEyebrow mode={mode} />
                    <InstallTitle mode={mode} />
                    <InstallLead mode={mode} />
                </div>
            </div>

            <InstallActions
                mode={mode}
                deferredPrompt={deferredPrompt}
                installing={installing}
                showSamsungFallback={showSamsungFallback}
                setShowSamsungFallback={setShowSamsungFallback}
                copied={copied}
                onNativeInstall={handleNative}
                onOpenChrome={openInChrome}
                onCopyLink={copyLink}
                onDismiss={dismiss}
                dismissLabel={mode === "chrome-manual" || mode === "ios" ? "Non mostrare più" : "Non ora"}
                testIdPrefix="install-banner"
            />
        </div>
    );
}
